import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, BookOpen, Pencil, Trash2, Paperclip, AlertTriangle, Upload, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  DataTable, Dialog, Field, FormRow, Tabs, ErrorState, TableSkeleton, fmtDate, plural,
} from '../../components/ui';
import StaffHeader from './StaffHeader';
import {
  useStaffRecord, useStableCallback, ExpiryBadge, IconButton, expiryStatus, expirySortKey, forgetStaffRecord,
} from './hrKit';

// Blue/grey for work in hand; amber is kept for "expiring", red for "expired".
const STATUS = {
  completed:   { label: 'Completed',   cls: 'badge-green' },
  in_progress: { label: 'In progress', cls: 'badge-blue' },
  pending:     { label: 'Pending',     cls: 'badge-gray' },
  expired:     { label: 'Expired',     cls: 'badge-red' },
};
const ORDER = { expired: 0, in_progress: 1, pending: 2, completed: 3 };
const MAX_MB = 15;

// A record past its expiry date is expired whatever status was saved on it.
const autoStatus = (r) => (expiryStatus(r.expiry_date).state === 'expired' ? 'expired' : (r.status || 'completed'));
const isFile = (url) => /^\/uploads\//.test(String(url || ''));

function TrainingDialog({ open, staffId, record, onSaved, onClose }) {
  const isEdit = !!record;
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setErrors({}); setFile(null);
    setForm(record ? {
      training_name: record.training_name || '',
      provider: record.provider || '',
      completed_date: record.completed_date ? String(record.completed_date).slice(0, 10) : '',
      expiry_date: record.expiry_date ? String(record.expiry_date).slice(0, 10) : '',
      certificate_url: record.certificate_url || '',
      status: record.status || 'completed',
      notes: record.notes || '',
    } : { training_name: '', provider: '', completed_date: todayKey, expiry_date: '', certificate_url: '', status: 'completed', notes: '' });
  }, [open, record]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!String(form.training_name || '').trim()) errs.training_name = 'Enter the training name.';
    if (form.expiry_date && form.completed_date && form.expiry_date < form.completed_date) errs.expiry_date = 'The expiry date is before the completed date.';
    if (file && file.size > MAX_MB * 1024 * 1024) errs.file = `The file is larger than ${MAX_MB} MB.`;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      let certificateUrl = form.certificate_url;
      if (file) {
        // The certificate is kept with the worker's documents, filed under the training's name.
        const fd = new FormData();
        fd.append('file', file);
        fd.append('staffId', staffId);
        fd.append('category', form.training_name.trim());
        if (form.expiry_date) fd.append('expiryDate', form.expiry_date);
        fd.append('skipAi', '1');
        fd.append('forTraining', '1');
        try {
          const { data } = await api.post('/staff/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          certificateUrl = data.file_path;
        } catch (err) {
          setErrors({ file: err.response?.data?.message || "The certificate didn't upload, so nothing was saved. Try again." });
          setSaving(false);
          return;
        }
      }
      const body = { ...form, certificate_url: certificateUrl || null };
      if (isEdit) await api.put(`/staff/${staffId}/training/${record.id}`, body);
      else await api.post(`/staff/${staffId}/training`, body);
      toast.success(isEdit ? 'Training record updated' : 'Training record added');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setErrors({ form: err.response?.data?.message || "The training record wasn't saved. Try again." });
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} size="md" title={isEdit ? 'Edit training record' : 'Add training record'}
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="staff-training-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add record')}</button>
        </>
      )}>
      <form id="staff-training-form" onSubmit={save} className="space-y-4">
        <Field label="Training" required error={errors.training_name}>
          {(id) => <input id={id} className="input-field" value={form.training_name || ''} onChange={(e) => set('training_name', e.target.value)} placeholder="e.g. First Aid Certificate" />}
        </Field>
        <Field label="Provider">
          {(id) => <input id={id} className="input-field" value={form.provider || ''} onChange={(e) => set('provider', e.target.value)} placeholder="e.g. St John Ambulance" />}
        </Field>
        <FormRow cols={3}>
          <Field label="Completed">
            {(id) => <input id={id} type="date" className="input-field" value={form.completed_date || ''} onChange={(e) => set('completed_date', e.target.value)} />}
          </Field>
          <Field label="Expires" error={errors.expiry_date}>
            {(id) => <input id={id} type="date" className="input-field" value={form.expiry_date || ''} onChange={(e) => set('expiry_date', e.target.value)} />}
          </Field>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.status || 'completed'} onChange={(e) => set('status', e.target.value)}>
                <option value="completed">Completed</option>
                <option value="in_progress">In progress</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
              </select>
            )}
          </Field>
        </FormRow>
        <Field label="Certificate" error={errors.file} hint="PDF or photo, up to 15 MB. It's also kept on the Documents tab.">
          {(fid) => (
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; if (errors.file) setErrors((x) => ({ ...x, file: undefined })); }} />
              {file ? (
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[13px] text-slate-700">
                  <Paperclip size={13} aria-hidden="true" /><span className="truncate">{file.name}</span>
                  <IconButton label="Remove file" icon={X} size={13} onClick={() => setFile(null)} className="!h-6 !w-6" />
                </span>
              ) : form.certificate_url ? (
                <span className="inline-flex max-w-full items-center gap-1.5 text-[13px]">
                  <Paperclip size={13} className="text-slate-400" aria-hidden="true" />
                  <a href={form.certificate_url} target="_blank" rel="noreferrer" className="truncate font-medium text-primary-700 hover:underline">
                    {isFile(form.certificate_url) ? 'Current certificate' : 'Current certificate link'}
                  </a>
                  <button type="button" onClick={() => set('certificate_url', '')} className="text-[12.5px] font-medium text-slate-500 hover:text-rose-700 hover:underline">Remove</button>
                </span>
              ) : null}
              <button id={fid} type="button" onClick={() => fileRef.current?.click()} className="btn-secondary btn-sm">
                <Upload size={13} aria-hidden="true" />{file || form.certificate_url ? 'Replace file' : 'Attach file'}
              </button>
            </div>
          )}
        </Field>
        <Field label="Notes">
          {(id) => <textarea id={id} className="input-field" rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />}
        </Field>
      </form>
    </Dialog>
  );
}

export default function StaffTraining() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get(`/staff/${id}/training`)
      .then((r) => setRecords(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load the training records. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const closeDialog = useStableCallback(() => { setDialogOpen(false); setEditingRecord(null); });
  const onSaved = () => { load(); forgetStaffRecord(id); };

  async function deleteRecord(r) {
    if (!await confirm(`${r.training_name} will be removed from this staff member's training records.`, { title: 'Delete training record?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/training/${r.id}`);
      toast.success('Training record deleted');
      setRecords((list) => list.filter((x) => x.id !== r.id));
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete the record. Try again."); }
  }

  const counts = useMemo(() => {
    const c = { all: records.length, completed: 0, in_progress: 0, pending: 0, expired: 0, expiring: 0 };
    for (const r of records) {
      const s = autoStatus(r);
      c[s] = (c[s] || 0) + 1;
      if (expiryStatus(r.expiry_date).state === 'soon') c.expiring += 1;
    }
    return c;
  }, [records]);

  const filtered = useMemo(() => {
    const list = filter === 'all' ? records
      : filter === 'expiring' ? records.filter((r) => expiryStatus(r.expiry_date).state === 'soon')
        : records.filter((r) => autoStatus(r) === filter);
    return [...list].sort((a, b) => (ORDER[autoStatus(a)] ?? 4) - (ORDER[autoStatus(b)] ?? 4) || expirySortKey(a.expiry_date) - expirySortKey(b.expiry_date));
  }, [records, filter]);

  const tabs = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'completed', label: 'Completed', count: counts.completed },
    { id: 'expiring', label: 'Expiring', count: counts.expiring },
    { id: 'expired', label: 'Expired', count: counts.expired },
    { id: 'in_progress', label: 'In progress', count: counts.in_progress },
    { id: 'pending', label: 'Pending', count: counts.pending },
  ];
  const FILTER_EMPTY = { completed: 'No completed training', expiring: 'Nothing expiring in the next 30 days', expired: 'No expired training', in_progress: 'Nothing in progress', pending: 'Nothing pending' };

  const columns = [
    { key: 'training', header: 'Training', render: (r) => (
      <div className="min-w-0">
        <p className="font-medium text-slate-900">{r.training_name}</p>
        {r.notes && <p className="mt-0.5 max-w-[260px] truncate text-[12px] text-slate-500" title={r.notes}>{r.notes}</p>}
      </div>
    ) },
    { key: 'provider', header: 'Provider', render: (r) => r.provider || <span className="text-slate-400">—</span> },
    { key: 'completed', header: 'Completed', render: (r) => <span className="whitespace-nowrap tabular-nums">{r.completed_date ? fmtDate(String(r.completed_date).slice(0, 10)) : <span className="text-slate-400">—</span>}</span> },
    { key: 'expires', header: 'Expires', render: (r) => <ExpiryBadge date={r.expiry_date} /> },
    { key: 'status', header: 'Status', render: (r) => { const st = STATUS[autoStatus(r)] || STATUS.pending; return <span className={`${st.cls} whitespace-nowrap`}>{st.label}</span>; } },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (r) => (
      <span className="flex items-center justify-end">
        {r.certificate_url && <IconButton label={`Open certificate for ${r.training_name}`} icon={Paperclip} href={r.certificate_url} target="_blank" rel="noreferrer" />}
        <IconButton label={`Edit ${r.training_name}`} icon={Pencil} onClick={() => { setEditingRecord(r); setDialogOpen(true); }} />
        <IconButton label={`Delete ${r.training_name}`} icon={Trash2} danger onClick={() => deleteRecord(r)} />
      </span>
    ) },
  ];

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />

      <div className="space-y-4">
        {counts.expiring > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
            <AlertTriangle size={15} className="shrink-0" aria-hidden="true" />
            <span className="flex-1">{plural(counts.expiring, 'training record')} {counts.expiring === 1 ? 'expires' : 'expire'} within 30 days.</span>
            {filter !== 'expiring' && <button type="button" onClick={() => setFilter('expiring')} className="text-[13px] font-medium underline">Show them</button>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="max-w-full overflow-x-auto"><Tabs variant="segmented" size="sm" ariaLabel="Training status" tabs={tabs} value={filter} onChange={setFilter} /></div>
          <button type="button" onClick={() => { setEditingRecord(null); setDialogOpen(true); }} className="btn-primary btn-sm ml-auto">
            <Plus size={14} aria-hidden="true" />Add training
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {loading ? <TableSkeleton rows={4} cols={5} />
            : loadError ? <ErrorState message={loadError} onRetry={load} />
              : (
                <DataTable
                  label="Training records"
                  columns={columns}
                  rows={filtered}
                  stickyHeader={false}
                  empty={records.length === 0
                    ? { icon: BookOpen, title: 'No training recorded yet', hint: 'Record courses and certificates so you can see when they need renewing.', action: <button type="button" onClick={() => { setEditingRecord(null); setDialogOpen(true); }} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Add first training record</button> }
                    : { icon: BookOpen, title: FILTER_EMPTY[filter] || 'No matching training', hint: `${plural(records.length, 'record')} in other groups.`, action: <button type="button" onClick={() => setFilter('all')} className="btn-secondary btn-sm">Show all</button> }}
                />
              )}
        </div>
      </div>

      <TrainingDialog open={dialogOpen} staffId={id} record={editingRecord} onSaved={onSaved} onClose={closeDialog} />
    </div>
  );
}
